import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Mail, Clock, Globe, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ContactInfo {
  phone: string;
  email: string;
  address: {
    street: string;
    city: string;
    region: string;
    postcode: string;
    country: string;
  };
  hours: string;
  serviceArea: string;
  visibility?: {
    phone?: boolean;
    email?: boolean;
    address?: boolean;
    hours?: boolean;
    serviceArea?: boolean;
  };
}

const Contact = () => {
  const { data: settings, isLoading } = useQuery({
    queryKey: ["site-settings", "contact_info"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "contact_info")
        .single();

      if (error) throw error;
      return data.setting_value as unknown as ContactInfo;
    },
  });

  const contactInfo: ContactInfo = settings || {
    phone: "+44 1234 567890",
    email: "info@4fox.uk",
    address: {
      street: "T & L Business Park, Winterstoke Road",
      city: "Weston-super-Mare",
      region: "Somerset",
      postcode: "BS23 3YS",
      country: "United Kingdom"
    },
    hours: "24/7 - Available Around the Clock",
    serviceArea: "Nationwide UK Coverage",
    visibility: {
      phone: true,
      email: true,
      address: true,
      hours: true,
      serviceArea: true,
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "@id": "https://4fox.uk/contact#webpage",
        "name": "Contact 4FOX Security",
        "description": "Contact page for 4FOX Security cyber consultancy services in Weston-super-Mare, Somerset",
        "url": "https://4fox.uk/contact"
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://4fox.uk/#localbusiness",
        "name": "4FOX Security",
        "description": "Cyber security consultancy providing fractional CISO services and cyber security expertise to UK businesses",
        "url": "https://4fox.uk",
        ...(contactInfo.visibility?.phone !== false && { "telephone": contactInfo.phone }),
        ...(contactInfo.visibility?.email !== false && { "email": contactInfo.email }),
        "priceRange": "££",
        ...(contactInfo.visibility?.address !== false && { "address": {
          "@type": "PostalAddress",
          "streetAddress": contactInfo.address.street,
          "addressLocality": contactInfo.address.city,
          "addressRegion": contactInfo.address.region,
          "postalCode": contactInfo.address.postcode,
          "addressCountry": "GB"
        }}),
        ...(contactInfo.visibility?.address !== false && { "geo": {
          "@type": "GeoCoordinates",
          "latitude": "51.3362752",
          "longitude": "-2.9548917"
        }}),
        ...(contactInfo.visibility?.address !== false && { "areaServed": [
          {
            "@type": "Country",
            "name": "United Kingdom"
          },
          {
            "@type": "State",
            "name": "Somerset"
          },
          {
            "@type": "State",
            "name": "North Somerset"
          },
          {
            "@type": "City",
            "name": "Weston-super-Mare"
          },
          {
            "@type": "City",
            "name": "Bristol"
          },
          {
            "@type": "State",
            "name": "South Gloucestershire"
          }
        ]}),
        ...(contactInfo.visibility?.hours !== false && { "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }}),
        "contactPoint": {
          "@type": "ContactPoint",
          ...(contactInfo.visibility?.phone !== false && { "telephone": contactInfo.phone }),
          "contactType": "customer service",
          ...(contactInfo.visibility?.email !== false && { "email": contactInfo.email }),
          "areaServed": "GB",
          "availableLanguage": "en"
        }
      }
    ]
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4 flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Us - Weston-super-Mare, Somerset | 4FOX Security"
        description="Contact 4FOX Security in Weston-super-Mare, Somerset. Expert cyber security consultancy serving UK businesses nationwide. Call us for a free consultation on fractional CISO services and cyber security solutions."
        canonical="https://4fox.uk/contact"
        localAreas={["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"]}
        schema={localBusinessSchema}
      />
      <Navbar />
      <div className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Our UK Cyber Security Team</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Based in Somerset, serving businesses across the United Kingdom. Contact us for a free cyber security consultation.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="md:col-span-1">
                <CardContent className="pt-6 space-y-6">
                  <div>
                    <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {contactInfo.visibility?.address !== false && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Address</p>
                          <p className="text-sm text-muted-foreground">
                            {contactInfo.address.street}<br />
                            {contactInfo.address.city}<br />
                            {contactInfo.address.region}<br />
                            {contactInfo.address.postcode}<br />
                            {contactInfo.address.country}
                          </p>
                        </div>
                      </div>
                    )}

                    {contactInfo.visibility?.phone !== false && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Phone</p>
                          <a href={`tel:${contactInfo.phone}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                            {contactInfo.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {contactInfo.visibility?.email !== false && (
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Email</p>
                          <a href={`mailto:${contactInfo.email}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>
                    )}

                    {contactInfo.visibility?.hours !== false && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Business Hours</p>
                          <p className="text-sm text-muted-foreground">{contactInfo.hours}</p>
                        </div>
                      </div>
                    )}

                    {contactInfo.visibility?.serviceArea !== false && (
                      <div className="flex items-start gap-3">
                        <Globe className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Service Area</p>
                          <p className="text-sm text-muted-foreground">{contactInfo.serviceArea}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="md:col-span-2">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
